import { BlockParameters, PHOTOVOLTAIC_STRATEGIES, SIM_SUN, UnobstructedMode, type PhotovoltaicStrategy } from '@visualizer/types';
import { AddMetadata, BlockKeys } from '../BlockCatalog';
import { StageContext } from '../Context';
import { PipelineBlock } from '../PipelineBlock';
import { PipelineStage } from '../Stage';
import { computeTotalEnergyFromSensors } from 'Utils/EnergyUtils';
import UnobstructedFilterStage from '../stages/filters/UnobstructedFilterStage';
import ValueThresholdStage from '../stages/filters/ValueThreshold';
import SortStage from '../stages/preprocess/SortStage';

type PvStageType = 'unobstructed' | 'sort' | 'threshold';
export type PvDemandPeakTarget = {
    id: string;
    layerId: string;
    layerName: string;
    simSetName: string;
    frame: number;
    peakLabel: string;
    peakRate: number;
    demand: number;
};

export type PvPotentialsBlockParameters = BlockParameters & {
    strategy?: PhotovoltaicStrategy;
    demand: number;
    panelEfficiency?: number;
    surfaceCoverage?: number;
    tolerance?: number;
    maxIterations?: number;
    peakTargets?: PvDemandPeakTarget[];
};

@AddMetadata({
    type: BlockKeys.PvPotentials,
    name: 'PV Supply',
    description: 'Smartly filters SPs to match energy demand',
    targetLayer: {
        simKind: SIM_SUN,
    },
})
class PvPotentialsBlock extends PipelineBlock<PvPotentialsBlockParameters> {
    // Declare --> map is populated in parent class and doesn't need to be re-initialized empty here:
    declare private stageByType: Map<PvStageType, PipelineStage<any>> | undefined;

    constructor(params: PvPotentialsBlockParameters, stringId?: string) {
        super(params, stringId);
    }

    protected override defineStages() {
        const stageByType = this.getStageByType();
        stageByType.clear();

        const unobstructedStage = new UnobstructedFilterStage({ unobstructedMode: UnobstructedMode.Corners, isValue: true });
        const sortStage = new SortStage();
        const thresholdStage = new ValueThresholdStage({ thresholdRange: [0, 100] });

        this.getStageByType().set('unobstructed', unobstructedStage);
        this.getStageByType().set('sort', sortStage);
        this.getStageByType().set('threshold', thresholdStage);

        return [unobstructedStage, sortStage, thresholdStage];
    }

    protected override executeStages(_stages: PipelineStage<any>[], context: StageContext): StageContext {
        switch (this.getParams().strategy) {
            case PHOTOVOLTAIC_STRATEGIES.MAX_YEARLY_PRODUCTION:
                return this.executeMaximizeYearlyProduction(context);
            case PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS:
                return this.executeMatchDemandPeaks(context);
            case PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND:
            default:
                return this.executeMatchYearlyDemand(context);
        }
    }

    private executeStageByKey(key: PvStageType, context: StageContext): StageContext {
        const stage = this.getStageByType().get(key);
        if (!stage) {
            throw new Error(`PvPotentialsBlock: Stage "${key}" is not registered.`);
        }
        const nextContext = stage.execute(context);
        return nextContext;
    }

    private getStageByType(): Map<PvStageType, PipelineStage<any>> {
        if (!this.stageByType) this.stageByType = new Map<PvStageType, PipelineStage<any>>();
        return this.stageByType;
    }

    /**
     * 1st Strategy: Maximize Yearly Demand
     *
     * Applies unobstructed filter to maximize the yearly production.
     *
     * @param context
     *
     * @returns Filtered Sensor Points that maximize the yearly production.
     */
    private executeMaximizeYearlyProduction(context: StageContext): StageContext {
        const unobstructedContext = this.executeStageByKey('unobstructed', context);
        return this.executeStageByKey('sort', unobstructedContext);
    }

    /**
     * 2nd Strategy: Match Yearly Demand
     *
     * Updates the threshold value to have the highest value SensorPoints matching the
     * demand in input.
     *
     * @returns Filtered Sensor Points that match the given yearly energy demand.
     */
    private executeMatchYearlyDemand(context: StageContext): StageContext {
        const unobstructedContext = this.executeStageByKey('unobstructed', context);
        const sortedContext = this.executeStageByKey('sort', unobstructedContext);
        const thresholdStage = this.getStageByType().get('threshold');
        if (!thresholdStage) {
            throw new Error('PvPotentialsBlock: Stage "threshold" is not registered.');
        }

        const demand = this.getParams().demand;
        const maxIterations = this.getParams().maxIterations ?? 20;
        const tolerance = this.getParams().tolerance ?? 1000;

        if (demand <= 0 || maxIterations <= 0) {
            const nextContext = thresholdStage.execute(sortedContext, { thresholdRange: [0, 100] } as never);
            return nextContext;
        }

        let low = 0;
        let high = 100;
        let lastValidMid = low;

        // Run threshold iteratively:
        for (let i = 0; i < maxIterations; i++) {
            const mid = (low + high) / 2;
            const thresholdedContext = thresholdStage.execute(sortedContext, { thresholdRange: [mid, 100] } as never);
            const matchedSupply = this.computeMatchedSupply(thresholdedContext);

            if (matchedSupply >= demand && Math.abs(matchedSupply - demand) <= tolerance) {
                lastValidMid = mid;
                break;
            }

            if (matchedSupply >= demand) {
                lastValidMid = mid;
                low = mid;
            } else {
                high = mid;
            }
        }

        const finalContext = thresholdStage.execute(sortedContext, { thresholdRange: [lastValidMid, 100] } as never);
        return finalContext;
    }

    /**
     * 3rd Strategy: Match Demand Peaks.
     *
     * Each target layer/frame is prepared upstream in Pipeline UI; per-layer execution
     * here matches the demand exactly like yearly-demand matching.
     */
    private executeMatchDemandPeaks(context: StageContext): StageContext {
        return this.executeMatchYearlyDemand(context);
    }

    private computeMatchedSupply(context: StageContext): number {
        const sensorsValue = computeTotalEnergyFromSensors(context.sensors);
        const panelEfficiency = (this.getParams().panelEfficiency ?? 100) / 100;
        const surfaceCoverage = (this.getParams().surfaceCoverage ?? 100) / 100;
        return sensorsValue * panelEfficiency * surfaceCoverage;
    }
}

export default PvPotentialsBlock;
